import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { addDays } from '../events/labels';
import { useEscapeLayer } from '../ui/Dialog';
import { MyItem, type MyAssignment } from './MyCalendarPage';
import { PointLine, splitTimeline } from '../requests/Timeline';
import { AWAITING_WORD, dayAnswerable, dayLabel, taskText, eventUrl, dayShortLabel, questionsOn, questionTitle, stampText, type DayCell, type MyQuestion, type RepeatEvery } from './myCalendar';
import type { MarkBody } from './useMyCalendar';

type Props = {
  cell: DayCell;
  /**
   * Owner 2026-10-29 00:21: several selected days — the panel's „Dostępny”, „Niedostępny”, „Godziny…” and „Wyczyść”
   * go to all of them (`cells`: those of them in the month shown). One day (or none given): the day of `cell`.
   */
  days?: readonly string[];
  cells?: readonly DayCell[];
  today: string;
  questions: readonly MyQuestion[];
  marking: boolean;
  markError: string | null;
  onMark: (body: MarkBody) => void;
  answering: { id: string; days: Record<string, boolean> } | null;
  answerError: string | null;
  onAnswer: (id: string, day: string, yes: boolean) => void;
  /** Owner 2026-11-03 23:45: „+ Zadanie prywatne” — the new task's dialog with this day; a task of the day — its dialog. */
  onNewTask?: (day: string) => void;
  onTask?: (id: string) => void;
  onClose: () => void;
};

/**
 * The day's panel (owner 2026-10-28 21:32: „zmiany i możliwość wpisania łatwo swoich dostępności”): docked to the right
 * edge of the window as the grid's drawer, the month keeping its width (owner 2026-10-29 00:23 / 00:53: „nie zwężaj
 * kalendarza tylko zrób to okno poza nim. przytul je do prawej tak jak w grafiku”).
 * „Moja dostępność” (Dostępny, Niedostępny, Godziny…, Wyczyść, Powtórz…), „Zapytania o ten dzień” (my answer „Tak” /
 * „Nie”, changeable while the question may still be answered), „Moje wydarzenia tego dnia” as the list shows them and
 * „Moje zadania prywatne” (owner 2026-11-03 23:45: the day's open private tasks and „+ Zadanie prywatne”).
 * A past day is read-only. Several selected days (owner 00:21): „Zaznaczone dni: N” — the availability buttons mark
 * them all; the questions and the events stay on each day's own panel (a click on a day that is not selected).
 */
export function MyDayPanel({ cell, days: picked, cells = [], today, questions, marking, markError, onMark, answering, answerError, onAnswer, onNewTask, onTask, onClose }: Props) {
  const title = useId();
  const day = cell.day;
  const multi = !!picked && picked.length > 1;
  const days = multi ? [...picked] : [day];
  const past = !multi && day < today;
  const [form, setForm] = useState<'hours' | 'repeat' | null>(null);
  useEscapeLayer(() => onClose(), true, { base: true });
  // review M9: the panel takes the focus (its heading) when it opens
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => heading.current?.focus(), []);
  // several days: a state is „on” only when every one of them has it (whole days)
  const marksOf = multi ? days.map((d) => cells.find((c) => c.day === d)?.mark) : [cell.mark];
  const same = marksOf.every((m) => m?.state === marksOf[0]?.state && !m?.hours) ? marksOf[0]?.state : undefined;
  const state = multi ? same : cell.mark?.state;
  const anyMarked = marksOf.some((m) => m) || marksOf.length < days.length;
  const asked = questionsOn(day, questions);
  return (
    <aside className="mc-panel" aria-labelledby={title} data-feature-id="crew.my_availability">
      <div className="mc-panel-head">
        <h2 id={title} ref={heading} tabIndex={-1}>
          {multi ? `Zaznaczone dni: ${days.length}` : dayLabel(day)}
        </h2>
        <button type="button" className="ep-close" aria-label="Zamknij" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="mc-panel-body">
        <section className="mc-sec" aria-labelledby={`${title}-a`}>
          <h3 id={`${title}-a`}>{multi ? 'Dostępność we wszystkich zaznaczonych dniach' : 'Moja dostępność'}</h3>
          {multi ? (
            <ul className="mc-preview" aria-label="Zaznaczone dni">
              {days.map((d) => (
                <li key={d}>{dayShortLabel(d)}</li>
              ))}
            </ul>
          ) : (
            <p className="mc-now">
              {state ? (state === 'available' ? 'Dostępny' : 'Niedostępny') : 'Nie oznaczono'}
              {cell.mark?.hours ? ` ${cell.mark.hours}` : state ? ' cały dzień' : ''}
            </p>
          )}
          {past ? (
            <p className="muted">Ten dzień minął — dostępności już się nie zmienia.</p>
          ) : (
            <>
              <div className="mc-actions">
                <button type="button" className="btn small" aria-pressed={state === 'available' && !cell.mark?.hours} disabled={marking} onClick={() => onMark({ days, state: 'available' })}>
                  Dostępny
                </button>
                <button type="button" className="btn small" aria-pressed={state === 'unavailable' && !cell.mark?.hours} disabled={marking} onClick={() => onMark({ days, state: 'unavailable' })}>
                  Niedostępny
                </button>
                <button type="button" className="btn small" aria-expanded={form === 'hours'} onClick={() => setForm(form === 'hours' ? null : 'hours')}>
                  Godziny…
                </button>
                <button type="button" className="btn small ghost" disabled={marking || !anyMarked} onClick={() => onMark({ days, state: null })}>
                  Wyczyść
                </button>
                {!multi && (
                  <button type="button" className="btn small ghost" aria-expanded={form === 'repeat'} onClick={() => setForm(form === 'repeat' ? null : 'repeat')}>
                    Powtórz…
                  </button>
                )}
              </div>
              {form === 'hours' && <HoursForm days={days} busy={marking} onMark={onMark} />}
              {form === 'repeat' && !multi && <RepeatForm day={day} busy={marking} onMark={onMark} />}
            </>
          )}
          {markError && (
            <p className="alert" role="alert">
              {markError}
            </p>
          )}
        </section>

        {multi ? (
          <p className="mc-sec muted">Zapytania i wydarzenia są w panelu jednego dnia: kliknij dzień, który nie jest zaznaczony.</p>
        ) : (
          <>
            <section className="mc-sec" aria-label="Zapytania o ten dzień">
              <h3>Zapytania o ten dzień</h3>
              {asked.length === 0 ? (
                <p className="muted">Nikt nie pytał o ten dzień.</p>
              ) : (
                <ul className="mc-questions">
                  {asked.map((q) => {
                    // owner 2026-10-30 21:31: the newest point of the correspondence — the decision these buttons change
                    const { latest } = splitTimeline(q.timeline);
                    return (
                      <li key={q.id}>
                        <QuestionHead q={q} />
                        {latest && latest.kind !== 'sent' && <PointLine point={latest} mine as="div" />}
                        <DayAnswer q={q} day={day} today={today} busy={answering?.id === q.id} onAnswer={onAnswer} />
                      </li>
                    );
                  })}
                </ul>
              )}
              {answerError && (
                <p className="alert" role="alert">
                  {answerError}
                </p>
              )}
            </section>

            <section className="mc-sec" aria-label="Moje wydarzenia tego dnia">
              <h3>Moje wydarzenia tego dnia</h3>
              {cell.entries.length === 0 && cell.awaiting.length === 0 ? (
                <p className="muted">Tego dnia nie pracujesz w żadnym wydarzeniu.</p>
              ) : (
                <div className="my-items">
                  {cell.entries.map((a: MyAssignment) => (
                    <MyItem key={`${a.id}:${a.startsAt ?? ''}`} a={a} />
                  ))}
                  {cell.awaiting.map((w) => (
                    <div key={`w:${w.questionId}`} className="my-item awaiting">
                      <span className="my-item-name">{w.canOpen && w.eventId ? <Link to={eventUrl(w.eventId)}>{w.eventName}</Link> : w.eventName}</span>
                      <span className="my-item-meta">
                        <span>{AWAITING_WORD}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {onNewTask && onTask && (
              <section className="mc-sec" aria-label="Moje zadania prywatne" data-feature-id="private-tasks.list">
                <h3>Moje zadania prywatne</h3>
                {cell.tasks.length === 0 ? (
                  <p className="muted">Nie masz zadań prywatnych na ten dzień.</p>
                ) : (
                  <ul className="mc-tasks">
                    {cell.tasks.map((t) => (
                      <li key={t.id}>
                        <button type="button" className="link-button" onClick={() => onTask(t.id)}>
                          {taskText(t)}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <button type="button" className="link-button mc-task-add" onClick={() => onNewTask(day)}>
                  + Zadanie prywatne
                </button>
              </section>
            )}
          </>
        )}
      </div>
    </aside>
  );
}

/** A question's name (a link to the event when I may open it), the message and when it was sent. */
export function QuestionHead({ q }: { q: MyQuestion }) {
  return (
    <div className="mc-q-head">
      <strong className="mc-q-title">{q.canOpen && q.eventId ? <Link to={eventUrl(q.eventId)}>{questionTitle(q)}</Link> : questionTitle(q)}</strong>
      <span className="mc-q-msg">{q.message}</span>
      <span className="mc-q-meta">Wysłano {stampText(q.sentAt)}</span>
    </div>
  );
}

/** Why a question cannot be answered any more (none: it can). */
export function lockedWhy(q: MyQuestion, day: string, today: string): string | null {
  if (q.superseded) return 'Zastąpione nowym zapytaniem.';
  if (q.closed) return 'Wydarzenie zostało odwołane — odpowiedź nie jest już potrzebna.';
  if (q.stale || q.days.find((d) => d.day === day)?.stale) return 'Termin wydarzenia się zmienił — poczekaj na nowe zapytanie.';
  if (!q.canAnswer || day < today) return 'Na to zapytanie nie można już odpowiedzieć.';
  return null;
}

/**
 * My decision on one day of a question: „Tak” / „Nie” (pressed = the day as it stands), read-only once it cannot be
 * answered. Owner 2026-10-30 21:31: it is the newest point of the question's history — earlier answers are never shown
 * rewritten here (they are in „Historia”).
 */
export function DayAnswer({ q, day, today, busy, onAnswer, showDay = false }: { q: MyQuestion; day: string; today: string; busy: boolean; onAnswer: (id: string, day: string, yes: boolean) => void; showDay?: boolean }) {
  const answer = q.days.find((d) => d.day === day)?.answer ?? null;
  const editable = dayAnswerable(q, day, today);
  const why = editable ? null : lockedWhy(q, day, today);
  return (
    <div className="mc-answer" role="group" aria-label={`Moja odpowiedź: ${dayShortLabel(day)}`}>
      {showDay && <span className="mc-answer-day">{dayShortLabel(day)}</span>}
      <button type="button" className="btn small mc-yes" aria-pressed={answer === true} disabled={!editable || busy} onClick={() => answer !== true && onAnswer(q.id, day, true)}>
        Tak
      </button>
      <button type="button" className="btn small mc-no" aria-pressed={answer === false} disabled={!editable || busy} onClick={() => answer !== false && onAnswer(q.id, day, false)}>
        Nie
      </button>
      {answer === null && editable && <span className="mc-q-meta">Czeka na Twoją odpowiedź</span>}
      {why && <span className="mc-q-meta">{why}</span>}
    </div>
  );
}

/** „Godziny…”: from–to, then „Dostępny / Niedostępny w tych godzinach” (an end before the start: after midnight). */
function HoursForm({ days, busy, onMark }: { days: string[]; busy: boolean; onMark: (body: MarkBody) => void }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const id = useId();
  const ready = /^\d{2}:\d{2}$/.test(from) && /^\d{2}:\d{2}$/.test(to) && from !== to;
  const save = (state: 'available' | 'unavailable') => onMark({ days, state, hours: { from, to } });
  return (
    <fieldset className="mc-form">
      <legend className="visually-hidden">Godziny</legend>
      <div className="mc-row">
        <label htmlFor={`${id}-f`}>Od</label>
        <input id={`${id}-f`} type="time" value={from} onChange={(e) => setFrom(e.target.value)} />
        <label htmlFor={`${id}-t`}>Do</label>
        <input id={`${id}-t`} type="time" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>
      <div className="mc-actions">
        <button type="button" className="btn small" disabled={!ready || busy} onClick={() => save('available')}>
          Dostępny w tych godzinach
        </button>
        <button type="button" className="btn small" disabled={!ready || busy} onClick={() => save('unavailable')}>
          Niedostępny w tych godzinach
        </button>
      </div>
    </fieldset>
  );
}

/** The last day „do dnia” may be: 12 months after the first (the server says the same). */
const maxUntil = (day: string) => {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const same = new Date(Date.UTC(y + 1, m - 1, d)).toISOString().slice(0, 10);
  return Number(same.slice(8, 10)) === d ? same : new Date(Date.UTC(y + 1, m, 0)).toISOString().slice(0, 10);
};

/**
 * „Powtórz…” (owner 21:37: „tylko powtórz (np co tydzień, co miesiąc) i tyle”): the state, co tydzień / co miesiąc,
 * „do dnia” (required) — the days it marks are listed before saving (from the server, the same arithmetic it saves
 * with; past days are left out). Nothing about the series is kept: it only marks these days.
 */
function RepeatForm({ day, busy, onMark }: { day: string; busy: boolean; onMark: (body: MarkBody) => void }) {
  const [state, setState] = useState<'available' | 'unavailable' | null>(null);
  const [every, setEvery] = useState<RepeatEvery>('week');
  const [until, setUntil] = useState('');
  const id = useId();
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(until) && until >= day && until <= maxUntil(day);
  const preview = useQuery({
    queryKey: ['crew', 'me', 'repeat', day, every, until],
    enabled: valid,
    queryFn: () => api<{ days: string[] }>(`/api/me/availability/repeat?day=${day}&every=${every}&until=${until}`),
  });
  const days = valid ? (preview.data?.days ?? []) : [];
  return (
    <fieldset className="mc-form" aria-label="Powtórz">
      <legend className="visually-hidden">Powtórz</legend>
      <div className="mc-row" role="radiogroup" aria-label="Oznacz jako">
        <label className="check">
          <input type="radio" name={`${id}-s`} checked={state === 'available'} onChange={() => setState('available')} /> Dostępny
        </label>
        <label className="check">
          <input type="radio" name={`${id}-s`} checked={state === 'unavailable'} onChange={() => setState('unavailable')} /> Niedostępny
        </label>
      </div>
      <div className="mc-row" role="radiogroup" aria-label="Jak często">
        <label className="check">
          <input type="radio" name={`${id}-e`} checked={every === 'week'} onChange={() => setEvery('week')} /> co tydzień
        </label>
        <label className="check">
          <input type="radio" name={`${id}-e`} checked={every === 'month'} onChange={() => setEvery('month')} /> co miesiąc
        </label>
      </div>
      <p className="mc-q-meta">{every === 'week' ? 'Ten sam dzień tygodnia.' : 'Ten sam dzień miesiąca (miesiąc bez takiego dnia jest pomijany).'}</p>
      <div className="mc-row">
        <label htmlFor={`${id}-u`}>do dnia</label>
        <input id={`${id}-u`} type="date" min={addDays(day, 1)} max={maxUntil(day)} value={until} required onChange={(e) => setUntil(e.target.value)} />
      </div>
      {until !== '' && !valid && <p className="mc-q-meta">Wybierz dzień po {dayShortLabel(day)}, najwyżej 12 miesięcy dalej.</p>}
      {preview.error && <p className="alert">{preview.error instanceof ApiError ? preview.error.message : 'Nie udało się policzyć dni'}</p>}
      {days.length > 0 && (
        <ul className="mc-preview" aria-label="Dni do oznaczenia">
          {days.map((d) => (
            <li key={d}>{dayShortLabel(d)}</li>
          ))}
        </ul>
      )}
      <div className="mc-actions">
        <button type="button" className="btn small primary" disabled={!state || days.length === 0 || busy} onClick={() => state && onMark({ days: [day], state, repeat: { every, until } })}>
          {days.length > 0 ? `Oznacz ${days.length} ${days.length === 1 ? 'dzień' : 'dni'}` : 'Oznacz dni'}
        </button>
      </div>
    </fieldset>
  );
}

